import { DEFENSE_K, type BaseStats } from '../sim/stats';
import type { Grade } from '../sim/types';

/**
 * Global balance model. Numbers grow ~9% per level so damage numbers feel big
 * at the cap (thousands per hit, tens of thousands on crits), while defence is
 * expressed as a fixed mitigation percentage per grade so it never balloons.
 */
export const MAX_LEVEL = 30;

export const growth = (level: number): number => Math.pow(1.09, Math.max(0, level - 1));

/** Attack power of a player at `level` before gear. */
export const playerAttackAt = (level: number): number => 100 * growth(Math.min(level, MAX_LEVEL));
export const playerHpAt = (level: number): number => 1000 * growth(Math.min(level, MAX_LEVEL));

/**
 * Item level a player is expected to have at a content level. Levels above
 * the cap encode endgame item-level gates: level 30 + (ilvl - 250) / 10.
 */
export const expectedIlvl = (level: number): number => (level <= MAX_LEVEL ? level * 8 : 250 + (level - MAX_LEVEL) * 10);
export const levelForIlvl = (ilvl: number): number => MAX_LEVEL + Math.max(0, (ilvl - 250) / 10);
/** Attack multiplier granted by gear at an item level. */
export const gearAttackMult = (ilvl: number): number => 1 + Math.max(0, ilvl) / 180;
/** HP multiplier granted by armour at an item level. */
export const gearHpMult = (ilvl: number): number => 1 + Math.max(0, ilvl) / 220;

/** Converts a mitigation fraction into a defence value for the damage formula. */
export const defenseFor = (mitigation: number): number => (DEFENSE_K * mitigation) / Math.max(0.01, 1 - mitigation);

export const GRADE_HP: Record<Grade, number> = { minion: 1.1, normal: 2.6, elite: 11, named: 32, boss: 260, raid: 520 };
export const GRADE_ATK: Record<Grade, number> = { minion: 0.7, normal: 1, elite: 1.35, named: 1.6, boss: 2.1, raid: 2.6 };
export const GRADE_MITIGATION: Record<Grade, number> = { minion: 0.05, normal: 0.1, elite: 0.15, named: 0.2, boss: 0.25, raid: 0.3 };

export interface MonsterStatMods {
  hp?: number;
  atk?: number;
  speed?: number;
  /** Overrides the item level the monster is tuned against. */
  ilvl?: number;
}

/**
 * Standard monster stat block for a grade at a level. HP is tuned against a
 * player with level-appropriate gear; above the level cap, `level` encodes an
 * item-level tier (see `expectedIlvl`).
 */
export function monsterStats(level: number, grade: Grade, mods: MonsterStatMods = {}): Partial<BaseStats> {
  const ilvl = mods.ilvl ?? expectedIlvl(level);
  const L = Math.min(level, MAX_LEVEL);
  return {
    maxHp: Math.round(playerAttackAt(L) * gearAttackMult(ilvl) * GRADE_HP[grade] * (mods.hp ?? 1)),
    maxMp: 0,
    attack: 42 * growth(L) * (1 + ilvl / 300) * GRADE_ATK[grade] * (mods.atk ?? 1),
    defense: defenseFor(GRADE_MITIGATION[grade]),
    critRate: 0.05,
    critDmg: 1.5,
    moveSpeed: 4.2 * (mods.speed ?? 1),
    atkSpeed: 1,
    mpRegenCombat: 0,
    mpRegenIdle: 0,
    hpRegen: 0,
  };
}

/** Class base stats before gear (gear multipliers are applied by the progression layer). */
export function classBaseStats(level: number, mods: { hp?: number; atk?: number; speed?: number; mp?: number; crit?: number } = {}): Partial<BaseStats> {
  return {
    maxHp: Math.round(playerHpAt(level) * (mods.hp ?? 1)),
    maxMp: Math.round(1000 * (mods.mp ?? 1)),
    attack: playerAttackAt(level) * (mods.atk ?? 1),
    defense: defenseFor(0.12),
    critRate: 0.1 + (mods.crit ?? 0),
    critDmg: 2,
    moveSpeed: 5 * (mods.speed ?? 1),
    atkSpeed: 1,
    cdr: 0,
    hpRegen: 0.02,
    mpRegenCombat: 25,
    mpRegenIdle: 80,
  };
}
